import React from 'react';
import { Button } from '../ui/button';
import { DynamicTable } from '../common/DynamicTable';
import { History, Pencil, Trash2 } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';

interface Contato {
  id: string;
  nome: string;
  telefone: string;
  email?: string;
  notas?: string;
  avatarUrl?: string;
  empresaId: string;
  instanciaId: string;
  dataCriacao: string;
  ultimaInteracao: string;
  tags?: Array<{ id: string; titulo: string; cor: string }>;
  [key: string]: any; // For any additional dynamic properties
}

interface ContatosTableProps {
  contatos: Contato[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: Extract<keyof Contato, string>, order: 'asc' | 'desc') => void;
  onSearch: (searchTerm: string) => void;
  onRowClick: (row: Contato) => void;
  onEdit: (contato: Contato) => void;
  onDelete: (contato: Contato) => void;
  onHistoricoZpro?: (contato: Contato) => void;
  canEdit: boolean;
}

const getFileUrl = (path?: string) => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  let API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
  if (!path.startsWith('/')) path = '/' + path;
  // Adicionar /uploads se não for URL absoluta
  if (!path.startsWith('/uploads')) path = '/uploads' + path;
  return `${API_URL}${path}`;
};

const getInitials = (name: string | null | undefined = '') => {
  return (name ?? '')
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'C';
};

export const ContatosTable: React.FC<ContatosTableProps> = ({
  contatos,
  loading,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  onRowClick,
  onEdit,
  onDelete,
  onHistoricoZpro,
  canEdit,
}) => {
  const columns = [
    {
      id: 'avatarUrl',
      label: 'Avatar',
      sortable: false,
      mobileHidden: true,
      format: (_: any, row: any) => (
        <Avatar className="h-8 w-8">
          <AvatarImage src={getFileUrl(row.avatarUrl)} alt={row.nome} />
          <AvatarFallback className="bg-gray-300 dark:bg-slate-600 text-white text-xs">
            {getInitials(row.nome)}
          </AvatarFallback>
        </Avatar>
      )
    },
    {
      id: 'nome',
      label: 'Nome',
      sortable: true
    },
    {
      id: 'telefone',
      label: 'Telefone',
      sortable: true
    },
    {
      id: 'email',
      label: 'E-mail',
      sortable: true
    },
    {
      id: 'tags',
      label: 'Tags',
      format: (_: any, row: any) => (
        <div className="flex flex-wrap gap-1">
          {row.tags?.map((tag: any) => (
            <span
              key={tag.id}
              className="px-2 py-1 text-xs rounded-full"
              style={{ backgroundColor: `${tag.cor}20`, color: tag.cor }}
            >
              {tag.titulo}
            </span>
          )) || '-'}
        </div>
      )
    },
    {
      id: 'dataCriacao',
      label: 'Criado em',
      format: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center' as const,
      format: (_: any, row: any) => (
        <div className="flex justify-center space-x-2">
          {onHistoricoZpro && row.idZpro && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onHistoricoZpro(row);
              }}
              className="dark:text-white dark:bg-slate-700"
              title="Histórico do Zpro"
            >
              <History className="h-4 w-4 mr-1" />
              Histórico
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="dark:text-white dark:bg-slate-700"
            disabled={!canEdit}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="dark:bg-red-600 dark:hover:bg-red-700"
            disabled={!canEdit}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      )
    }
  ];

  return (
    <DynamicTable
      columns={columns}
      data={contatos}
      loading={loading}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onSort={onSort}
      onSearch={onSearch}
      onRowClick={onRowClick}
      mobileCards
      searchPlaceholder="Pesquisar contatos..."
    />
  );
};
